import React, { useState } from "react";

import { useHistory, useParams } from "react-router-dom";

import useGetPortfolioPlanAssignments from "@fartherfinance/frontend/api/Investment/hooks/usePortfolioPlanAssignment";
import usePostExcludedAssets from "@fartherfinance/frontend/api/Investment/hooks/usePostExcludedAssets";
import {
  ClientId,
  PortfolioPlanAssignmentId,
  PortfolioPlanId,
} from "@fartherfinance/frontend/api/Types";

import PortfoliosV2Accounts from "../Accounts";
import useRequestAuth from "@src/multiCustodian/hooks/useRequestAuth";
import useStatusNotification from "@src/multiCustodian/hooks/useStatusNotification";
import Conditional from "@src/sharedComponents/Conditional/Conditional";
import Modal from "@src/sharedComponents/Modal/Modal";

import DoNotSellModal from "./DoNotSellModal";
import { EmptyState, ErrorState } from "./ErrorState";
import { createCompleteExclusionsList } from "./helpers";
import useDoNotSellTableData from "./hooks/useDoNotSellTableData";
import usePortfolioHoldingsWithExclusions from "./hooks/usePortfolioHoldingsWithExclusions";
import LoadingState from "./LoadingState";

import styles from "./DoNotSellModal.module.css";

const DoNotSell: React.FC = () => {
  const { portfolioPlanId, clientId, assignmentId } = useParams<{
    portfolioPlanId: PortfolioPlanId;
    clientId: ClientId;
    assignmentId: PortfolioPlanAssignmentId;
  }>();
  const history = useHistory();

  const clientAuth = useRequestAuth();
  const showNotification = useStatusNotification();

  const { mutateAsync } = usePostExcludedAssets(
    portfolioPlanId,
    assignmentId,
    clientAuth
  );

  // Get assignment data to get the trading group name
  const { data: assignmentData } = useGetPortfolioPlanAssignments(
    {
      abstractPortfolioId: portfolioPlanId,
      portfolioPlanAssignmentId: assignmentId,
    },
    clientAuth
  );

  const {
    securities,
    localExclusions,
    isLoading,
    hasError,
    toggleExclusion,
    markAllSecurities,
    unmarkAllSecurities,
    setLocalExclusions,
    numberOfChanges,
  } = usePortfolioHoldingsWithExclusions(
    clientId,
    portfolioPlanId,
    assignmentId
  );

  const handleCloseModal = () => {
    history.push(
      `/Client/${clientId}/Investments/PortfoliosV2/${portfolioPlanId}/Accounts`
    );
  };

  const [isMutating, setIsMutating] = useState(false);

  const handleConfirmModal = async () => {
    try {
      setIsMutating(true);

      // Use our helper function to create a complete list of excluded securities
      // It will combine existing exclusions with client changes and return API-ready objects
      const securityIds = createCompleteExclusionsList(
        securities,
        localExclusions
      );

      await mutateAsync(securityIds);

      showNotification("Exclusions saved successfully", "Success");
      setLocalExclusions({});

      handleCloseModal();
    } catch (err) {
      console.error("Error saving exclusions:", err);
      showNotification("Error saving exclusions", "Error");
    } finally {
      setIsMutating(false);
    }
  };

  const [inputValue, setInputValue] = useState("");

  const filteredList = securities.filter((security) => {
    if (inputValue.trim() === "") {
      return true;
    }

    const securityName = security.ticker?.toLowerCase();
    const description = security.description?.toLowerCase();

    if (
      securityName?.includes(inputValue.toLowerCase()) ||
      description?.includes(inputValue.toLowerCase())
    ) {
      return true;
    }

    return false;
  });

  const { columns, rows } = useDoNotSellTableData(
    filteredList,
    localExclusions,
    toggleExclusion
  );

  return (
    <PortfoliosV2Accounts>
      <Modal
        closeModal={handleCloseModal}
        modalStyle={{ width: "90%", maxWidth: "1024px" }}
        className={styles.content}
        showCloseButton
      >
        <Conditional condition={isLoading}>
          <LoadingState />
        </Conditional>
        <Conditional condition={hasError}>
          <ErrorState
            isMutating={true}
            numberChanges={0}
            onCloseModal={handleCloseModal}
            onConfirm={() => undefined}
          />
        </Conditional>
        <Conditional
          condition={!isLoading && !hasError && securities.length === 0}
        >
          <EmptyState
            isMutating={true}
            numberChanges={0}
            onCloseModal={handleCloseModal}
            onConfirm={() => undefined}
          />
        </Conditional>
        <Conditional
          condition={!isLoading && !hasError && securities.length > 0}
        >
          <DoNotSellModal
            inputValue={inputValue}
            setInputValue={setInputValue}
            rows={rows}
            columns={columns}
            isMutating={isMutating}
            closeModal={handleCloseModal}
            onConfirm={handleConfirmModal}
            tradingGroupName={assignmentData?.name || "Trading Group"}
            numberOfChanges={numberOfChanges}
            markAllSecurities={markAllSecurities}
            unmarkAllSecurities={unmarkAllSecurities}
          />
        </Conditional>
      </Modal>
    </PortfoliosV2Accounts>
  );
};

export default DoNotSell;
